import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Autocomplete,
  Avatar,
  CircularProgress,
  Typography,
  MenuItem,
  Chip,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { searchMembers, updateMember, fetchMemberById } from '../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';

const SECTION_TITLE = {
  personal: 'Kişisel bilgileri düzenle',
  social: 'Sosyal medyayı düzenle',
  education: 'Eğitim bilgilerini düzenle',
  club: 'Kulüp bilgilerini düzenle',
};

/** EditProjectDialog / CreateProjectDialog ile aynı outlined input stili */
export const DIALOG_INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '5px',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0E4D94',
    },
  },
  '& .MuiInputBase-input': {
    py: 1.2,
    px: 1.5,
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e6e6e6',
  },
};

export const DIALOG_MULTILINE_SX = {
  ...DIALOG_INPUT_SX,
  '& .MuiInputBase-input': {
    fontSize: '14px',
    lineHeight: 1.5,
  },
};

/** Üstte sabit etiket + altta input (projeler modallarındaki pattern) */
export const LabeledControl = ({ label, children }) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

/** Mevcut üye kaydından API’nin beklediği tam gövdeyi üretir; yalnızca bir bölüm düzenlenirken diğer alanlar buradan korunur. */
function buildPayloadFromMember(member) {
  const full = (member.fullName || '').trim();
  const nameTokens = full.split(/\s+/).filter(Boolean);
  const derivedLast = nameTokens.length > 1 ? nameTokens[nameTokens.length - 1] : '';
  const derivedFirst = nameTokens.length > 1 ? nameTokens.slice(0, -1).join(' ') : (nameTokens[0] || '');

  return {
    id: member.id,
    groupId: member.groupId,
    firstName: member.firstName || derivedFirst,
    lastName: member.lastName || derivedLast,
    email: member.email ?? '',
    phoneNumber: member.phoneNumber ?? '',
    address: member.address ?? '',
    workAddress: member.workAddress ?? '',
    jobTitle: member.jobTitle ?? '',
    company: member.company ?? '',
    sector: member.sector ?? '',
    university: member.university ?? '',
    highSchool: member.highSchool ?? '',
    referenceMemberId: member.referenceMemberId ?? null,
    membershipType: member.membershipType ?? 0,
    tcKimlikNo: member.tcKimlikNo || null,
    birthDate: member.birthDate ? new Date(member.birthDate).toISOString() : null,
    clubJoinDate: member.clubJoinDate ? new Date(member.clubJoinDate).toISOString() : null,
    website: member.website?.trim() || null,
    hobbies: member.hobbies?.trim() ? member.hobbies : null,
    facebook: member.facebook?.trim() || null,
    twitter: member.twitter?.trim() || null,
    linkedIn: member.linkedIn?.trim() || null,
    logoCariCode: member.logoCariCode?.trim() || null,
  };
}

const EditMemberDialog = ({ open, onClose, member, onUpdated, section }) => {
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    jobTitle: '',
    company: '',
    sector: '',
    university: '',
    highSchool: '',
    address: '',
    workAddress: '',
    tcKimlikNo: '',
    birthDate: '',
    clubJoinDate: '',
    website: '',
    facebook: '',
    twitter: '',
    linkedIn: '',
    logoCariCode: '',
  });
  const [membershipType, setMembershipType] = useState(0);
  const [reference, setReference] = useState(null);
  const [refQuery, setRefQuery] = useState('');
  const [refLoading, setRefLoading] = useState(false);
  const [refOptions, setRefOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [hobbyList, setHobbyList] = useState([]);
  const [hobbyInput, setHobbyInput] = useState('');

  useEffect(() => {
    if (!open || !member) return;
    setLoading(false);
    const full = (member.fullName || '').trim();
    const nameTokens = full.split(/\s+/).filter(Boolean);
    const derivedLast = nameTokens.length > 1 ? nameTokens[nameTokens.length - 1] : '';
    const derivedFirst = nameTokens.length > 1 ? nameTokens.slice(0, -1).join(' ') : (nameTokens[0] || '');

    setValues({
      firstName: member.firstName || derivedFirst,
      lastName: member.lastName || derivedLast,
      email: member.email || '',
      phoneNumber: member.phoneNumber || '',
      jobTitle: member.jobTitle || '',
      company: member.company || '',
      sector: member.sector || '',
      university: member.university || '',
      highSchool: member.highSchool || '',
      address: member.address || '',
      workAddress: member.workAddress || '',
      tcKimlikNo: member.tcKimlikNo || '',
      birthDate: member.birthDate ? member.birthDate.substring(0, 10) : '',
      clubJoinDate: member.clubJoinDate ? member.clubJoinDate.substring(0, 10) : '',
      website: member.website || '',
      facebook: member.facebook || '',
      twitter: member.twitter || '',
      linkedIn: member.linkedIn || '',
      logoCariCode: member.logoCariCode || '',
    });

    setMembershipType(member.membershipType ?? 0);

    setReference(null);
    setRefQuery('');
    setRefOptions([]);
    setHobbyList(member.hobbies ? member.hobbies.split(',').map((s) => s.trim()).filter(Boolean) : []);
    setHobbyInput('');

    if (section === 'club' && member.referenceMemberId) {
      fetchMemberById(member.referenceMemberId)
        .then((ref) => {
          if (!ref) return;
          setReference({ id: ref.id, fullName: ref.fullName, profilePictureUrl: ref.profilePictureUrl });
        })
        .catch(() => {});
    }
  }, [open, member, section]);

  useEffect(() => {
    if (!refQuery || refQuery.length < 2) { setRefOptions([]); return; }
    let active = true;
    setRefLoading(true);
    searchMembers(refQuery)
      .then((data) => { if (active) setRefOptions(data || []); })
      .catch(() => { if (active) setRefOptions([]); })
      .finally(() => { if (active) setRefLoading(false); });
    return () => { active = false; };
  }, [refQuery]);

  const handleChange = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const handleClose = () => {
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!member || !section) return;
    setLoading(true);
    try {
      const base = buildPayloadFromMember(member);
      let patch = {};
      switch (section) {
        case 'personal':
          patch = {
            firstName: values.firstName?.trim(),
            lastName: values.lastName?.trim(),
            email: values.email,
            phoneNumber: values.phoneNumber,
            address: values.address,
            workAddress: values.workAddress,
            jobTitle: values.jobTitle,
            company: values.company,
            sector: values.sector,
            tcKimlikNo: values.tcKimlikNo || null,
            birthDate: values.birthDate ? new Date(values.birthDate).toISOString() : null,
            website: values.website?.trim() || null,
            hobbies: hobbyList.length ? hobbyList.join(', ') : null,
            logoCariCode: values.logoCariCode?.trim() || null,
          };
          break;
        case 'social':
          patch = {
            facebook: values.facebook?.trim() || null,
            twitter: values.twitter?.trim() || null,
            linkedIn: values.linkedIn?.trim() || null,
          };
          break;
        case 'education':
          patch = {
            university: values.university,
            highSchool: values.highSchool,
          };
          break;
        case 'club':
          patch = {
            clubJoinDate: values.clubJoinDate ? new Date(values.clubJoinDate).toISOString() : null,
            membershipType,
            referenceMemberId: reference?.id ?? null,
          };
          break;
        default:
          break;
      }
      const payload = { ...base, ...patch };
      const updated = await updateMember(payload);
      setLoading(false);
      onUpdated?.(updated);
      handleClose();
    } catch (err) {
      setLoading(false);
    }
  };

  if (!section) return null;

  const dialogTitle = SECTION_TITLE[section] || 'Üyeyi düzenle';

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (loading) return;
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        {dialogTitle}
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={loading}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            {section === 'personal' && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Ad">
                      <TextField
                        value={values.firstName}
                        onChange={handleChange('firstName')}
                        required
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Soyad">
                      <TextField
                        value={values.lastName}
                        onChange={handleChange('lastName')}
                        required
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="E-posta">
                      <TextField
                        value={values.email}
                        onChange={handleChange('email')}
                        type="email"
                        required
                        fullWidth
                        disabled={loading}
                        autoComplete="email"
                        placeholder=""
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Telefon">
                      <TextField
                        value={values.phoneNumber}
                        onChange={handleChange('phoneNumber')}
                        required
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Ünvan">
                      <TextField
                        value={values.jobTitle}
                        onChange={handleChange('jobTitle')}
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Şirket">
                      <TextField
                        value={values.company}
                        onChange={handleChange('company')}
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                </Stack>
                <LabeledControl label="Sektör">
                  <TextField
                    value={values.sector}
                    onChange={handleChange('sector')}
                    fullWidth
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="TC Kimlik No">
                      <TextField
                        value={values.tcKimlikNo}
                        onChange={handleChange('tcKimlikNo')}
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        inputProps={{ maxLength: 11 }}
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Doğum tarihi">
                      <TextField
                        type="date"
                        value={values.birthDate}
                        onChange={handleChange('birthDate')}
                        fullWidth
                        disabled={loading}
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                </Stack>
                <LabeledControl label="Ev Adresi">
                  <TextField
                    value={values.address}
                    onChange={handleChange('address')}
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_MULTILINE_SX}
                  />
                </LabeledControl>
                <LabeledControl label="İş adresi">
                  <TextField
                    value={values.workAddress}
                    onChange={handleChange('workAddress')}
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={loading}
                    placeholder=""
                    sx={DIALOG_MULTILINE_SX}
                  />
                </LabeledControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Web sitesi">
                      <TextField
                        value={values.website}
                        onChange={handleChange('website')}
                        fullWidth
                        disabled={loading}
                        placeholder="https://"
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Logo cari kod">
                      <TextField
                        value={values.logoCariCode}
                        onChange={handleChange('logoCariCode')}
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        inputProps={{ maxLength: 60 }}
                        helperText="Cari hareket ve muhasebe bağlantısı için."
                        FormHelperTextProps={{ sx: { mt: 0.75, fontSize: '12px' } }}
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                </Stack>
                <LabeledControl label="Hobiler">
                  <TextField
                    value={hobbyInput}
                    onChange={(e) => setHobbyInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = hobbyInput.trim();
                        if (v && !hobbyList.includes(v)) {
                          setHobbyList((prev) => [...prev, v]);
                          setHobbyInput('');
                        }
                      }
                    }}
                    fullWidth
                    disabled={loading}
                    placeholder="Hobi yazıp Enter ile ekleyin"
                    helperText="Listeden çıkarmak için çip üzerindeki × simgesine tıklayın."
                    FormHelperTextProps={{ sx: { mt: 0.75, fontSize: '12px' } }}
                    sx={DIALOG_INPUT_SX}
                  />
                  {hobbyList.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                      {hobbyList.map((h) => (
                        <Chip
                          key={h}
                          label={h}
                          size="small"
                          variant="outlined"
                          onDelete={() => setHobbyList((prev) => prev.filter((x) => x !== h))}
                          sx={{ fontSize: '12px', borderColor: '#e6e6e6' }}
                        />
                      ))}
                    </Stack>
                  )}
                </LabeledControl>
              </>
            )}

            {section === 'social' && (
              <>
                <LabeledControl label="Facebook">
                  <TextField
                    value={values.facebook}
                    onChange={handleChange('facebook')}
                    fullWidth
                    disabled={loading}
                    placeholder="URL"
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
                <LabeledControl label="Twitter">
                  <TextField
                    value={values.twitter}
                    onChange={handleChange('twitter')}
                    fullWidth
                    disabled={loading}
                    placeholder="URL"
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
                <LabeledControl label="LinkedIn">
                  <TextField
                    value={values.linkedIn}
                    onChange={handleChange('linkedIn')}
                    fullWidth
                    disabled={loading}
                    placeholder="URL"
                    sx={DIALOG_INPUT_SX}
                  />
                </LabeledControl>
              </>
            )}

            {section === 'education' && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Üniversite">
                      <TextField
                        value={values.university}
                        onChange={handleChange('university')}
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Lise">
                      <TextField
                        value={values.highSchool}
                        onChange={handleChange('highSchool')}
                        fullWidth
                        disabled={loading}
                        placeholder=""
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                </Stack>
              </>
            )}

            {section === 'club' && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Kulübe giriş tarihi">
                      <TextField
                        type="date"
                        value={values.clubJoinDate}
                        onChange={handleChange('clubJoinDate')}
                        fullWidth
                        disabled={loading}
                        sx={DIALOG_INPUT_SX}
                      />
                    </LabeledControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LabeledControl label="Üyelik tipi">
                      <TextField
                        select
                        value={membershipType}
                        onChange={(e) => setMembershipType(Number(e.target.value))}
                        required
                        fullWidth
                        disabled={loading}
                        sx={DIALOG_INPUT_SX}
                      >
                        <MenuItem value={0}>Devamlı Üye</MenuItem>
                        <MenuItem value={1}>Devamdan Muaf Üye</MenuItem>
                        <MenuItem value={2}>Aidattan Muaf Üye</MenuItem>
                        <MenuItem value={3}>Onursal Üye</MenuItem>
                        <MenuItem value={4}>İzinli Üye</MenuItem>
                      </TextField>
                    </LabeledControl>
                  </Box>
                </Stack>
                <LabeledControl label="Referans üye">
                  <Autocomplete
                    options={refOptions}
                    loading={refLoading}
                    getOptionLabel={(o) => o?.fullName || ''}
                    value={reference}
                    onChange={(e, v) => setReference(v)}
                    onInputChange={(e, v) => setRefQuery(v)}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    noOptionsText="Sonuç yok"
                    loadingText="Aranıyor..."
                    fullWidth
                    disabled={loading}
                    sx={{ minWidth: { xs: '100%', sm: 480 } }}
                    renderOption={(props, option) => (
                      <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id}>
                        <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 24, height: 24 }} />
                        {option.fullName}
                      </Stack>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        hiddenLabel
                        placeholder="İsim yazın..."
                        disabled={loading}
                        sx={DIALOG_INPUT_SX}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {refLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </LabeledControl>
                {reference && (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar src={resolveGroupsAssetUrl(reference.profilePictureUrl)} sx={{ width: 40, height: 40 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{reference.fullName}</Typography>
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#0a3d7a',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditMemberDialog;
